import { create } from 'zustand';
import type { SortField, SortOrder } from '../api/types.ts';

export type ViewMode = 'list' | 'grid';
export type Theme = 'system' | 'light' | 'dark';

interface UiState {
  view: ViewMode;
  sort: SortField;
  order: SortOrder;
  theme: Theme;
  sidebarOpen: boolean;
  drawerTab: 'info' | 'history' | 'share';
  setView: (v: ViewMode) => void;
  setSort: (field: SortField) => void;
  setTheme: (t: Theme) => void;
  toggleSidebar: () => void;
  setDrawerTab: (t: UiState['drawerTab']) => void;
}

const KEY = 'yumsync.ui';

interface Saved { view?: ViewMode; sort?: SortField; order?: SortOrder; theme?: Theme }

function load(): Saved {
  try {
    return JSON.parse(localStorage.getItem(KEY) ?? '{}') as Saved;
  } catch {
    return {};
  }
}

function save(state: Saved): void {
  try { localStorage.setItem(KEY, JSON.stringify(state)); } catch { /* 저장 실패는 무시 */ }
}

export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  if (theme === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', theme);
}

const saved = load();
applyTheme(saved.theme ?? 'system');

export const useUi = create<UiState>((set, get) => ({
  view: saved.view ?? 'list',
  sort: saved.sort ?? 'name',
  order: saved.order ?? 'asc',
  theme: saved.theme ?? 'system',
  sidebarOpen: false,
  drawerTab: 'info',

  setView: (view) => { set({ view }); save({ ...get(), view }); },

  setSort: (field) => {
    const { sort, order } = get();
    const next: SortOrder = sort === field && order === 'asc' ? 'desc' : 'asc';
    set({ sort: field, order: next });
    save({ ...get(), sort: field, order: next });
  },

  setTheme: (theme) => { applyTheme(theme); set({ theme }); save({ ...get(), theme }); },
  toggleSidebar: () => { set({ sidebarOpen: !get().sidebarOpen }); },
  setDrawerTab: (drawerTab) => { set({ drawerTab }); },
}));
